<template>
	<section :class="`marketing-section ${sectionBackgroundColor}`">
		<div class="container">
			<div class="display-flex flex-direction-column-smo justify-content-space-between position-relative">
				<div
					:class="[
						'col-5-md pr-300-md mt-200-md',
						{
							'text-color-white': isDarkMode,
							'position-sticky-md': !appStore.isMobile
						}
					]"
				>
					<h2 :class="[appStore.isMobile ? 'heading-1' : 'heading-xl']">Why private real&nbsp;estate</h2>
					<p class="body-xl">
						Historically, private market real estate has featured a combination of traits not found in other
						asset classes: long-term earning potential and effective diversification beyond the stock
						market.
					</p>
				</div>
				<div class="col-7-md col-6-xl mt-300-smo display-flex flex-direction-column gap-150">
					<div
						v-for="(feature, index) in features"
						:key="index"
						:class="[
							`${featureColorClasses} p-300-md p-200
							display-flex
							gap-150
							flex-direction-column-reverse-smo module-radius-lg`
						]"
					>
						<div>
							<h3 class="heading-3" v-html="feature.heading" />
							<p>{{ feature.description }}</p>
						</div>
						<BaseSvgIcon :name="feature.iconName" width="96" height="96" />
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAppStore } from '@stores/app';

interface Props {
	theme?: 'light' | 'dark';
}

const props = withDefaults(defineProps<Props>(), {
	theme: 'dark'
});

const appStore = useAppStore();

const features: Array<{ heading: string; description: string; iconName: string }> = [
	{
		heading: 'Wealth preservation <br/> <i>and</i> growth',
		description:
			'Alternative assets like private real estate have historically offered a unique combination of lower volatility than stocks and higher potential returns than bonds. This mixture can anchor your portfolio, smoothing out the wild ups and downs of the public markets.',
		iconName: props.theme === 'dark' ? 'hd-investment-folder-dark-mode' : 'hd-investment-folder'
	},
	{
		heading: 'Income generation',
		description:
			'For many investors, the ability to create consistent income—via equity ownership in apartment buildings or single-family rentals that earn income through rental payments, for instance—is one of the most attractive aspects of real estate investing.',
		iconName: props.theme === 'dark' ? 'hd-money-stack' : 'hd-coin-stack'
	},
	{
		heading: 'Superior diversification',
		description:
			'Private market assets are less likely to be affected by market fluctuations, helping reduce risk and improving your long-term financial stability—even during sustained periods of economic uncertainty.',
		iconName: props.theme === 'dark' ? 'hd-portfolio-wheel-dark-mode' : 'hd-portfolio-wheel'
	},
	{
		heading: 'Built for the future',
		description:
			'Our portfolio aims to harness the most powerful long-term macroeconomic drivers of the U.S. economy. Trends like increased demand for well-located residential assets across the sunbelt to the explosion of eCommerce-driven industrial spaces.',
		iconName: 'hd-re-build-for-rent'
	}
];

const isDarkMode = computed((): boolean => props.theme === 'dark');

const sectionBackgroundColor = computed((): string =>
	isDarkMode.value ? 'bg-color-content-primary' : 'bg-color-gray-20'
);

const featureColorClasses = computed((): string =>
	isDarkMode.value ? 'bg-color-content-primary text-color-white' : 'bg-color-white'
);
</script>

<style lang="scss" scoped>
.position-sticky-md {
	top: 200px;
}

.svg-icon {
	fill: none;
}
</style>
